import { autoinject, computedFrom, transient, useView } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService } from "../../../../../../../../services/basics/notification-client.service";
import { VettingService } from "../../../../../../../../services/vetting/vetting.service";
import * as Enumerable from "linq";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { RuleActionModel, VetClientModel, VetRiskScoreModel, VetRuleOutcomeStepEnum, VetRuleRunClientModel, VetStatusEnum, VettingActionStepEnum } from "../../../../../../../../models/vetting/vetting.models";
import { ChildComponent } from "../../../../../child-component";
import { LookupClientService } from "../../../../../../../../services/lookups/lookup-client.service";
import { LookupValueRequest } from "../../../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../../../constants/modules/lookup.constants";
import { type CompRightStoreProductModel } from "../../../../../../../../models/rightstore/rightstore.models";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

class OutcomeModel {
    class: string;
    title: string;
    description: string;
    noActionsRequired: boolean;
}

@autoinject
@transient()
@useView('views/pages/modules/auto-screening/templates/pre-charter-healthcheck/tabs/overview/pre-charter-healthcheck-overview.html')
export class PreCharterHealthcheckScreeningOverview extends ChildComponent<VetClientModel> {

    outcome: OutcomeModel;
    latestRun: VetRuleRunClientModel;
    vetRiskScores: VetRiskScoreModel[] = [];
    suggestedActions: RuleActionModel[] = [];
    criticalActions: RuleActionModel[] = [];
    requiredActions: RuleActionModel[] = [];
    isLoading: boolean;

    constructor(notificationsService: NotificationClientService, private vettingService: VettingService,
        private dialogClientService: DialogClientService, private lookupService: LookupClientService,
        i18N: I18N, private configService: ConfigService) {
        super(notificationsService, i18N);
    }

    async attached() {
        await this.reload();
    }

    @computedFrom("item", "item.vetId")
    get vesselHealthReviewProduct(): CompRightStoreProductModel {
        return {
            name: 'Vessel Health Review',
            description: 'Best in class marine assurance services for your risk management.',
            tags: null,
            url: this.vesselHealthReviewUri,
            imageUrl: 'src="../../../../../images/rightstore/vessel-health-review.png',
            isComingSoon: false,
            expiry: '',
            showInPlatform: true
        }
    }

    openRiskAnalysisRulesDialog(vetRiskScore: VetRiskScoreModel) {
        if ((vetRiskScore.countLowFail + vetRiskScore.countMediumFail + vetRiskScore.countHighFail + vetRiskScore.countOraFail) > 0) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RiskAnalysisRulesDialog,
                {
                    showPassed: false,
                    vhc: true,
                    riskCategoryId: vetRiskScore.riskCategoryId,
                    categoryName: vetRiskScore.name,
                    vetId: this.item.vetId,
                    vesselName: this.item.vesselName,
                    vesselIdentifier: this.item.internationalMaritimeOrganizationNumber
                }, true, false, false, "right");
        }
    }

    @computedFrom("item", "item.vetId")
    get vesselHealthReviewUri() {
        return this.configService.config.urls.vesselHealthReviewUrl + (this.item && this.item.vetId > 0 ? `&vetId=${this.item.vetId}` : '');
    }

    async reload() {
        this.isLoading = true;
        const runs = await this.vettingService.getVetRuleRunsClientModel(this.item.vetId);
        if (runs && runs.length > 0) {
            this.latestRun = Enumerable.from(runs).orderByDescending(x => x.date).firstOrDefault();
        }

        this.vetRiskScores = Enumerable.from(this.item.vetRiskScores).where(x => x.name != 'All' && x.name != 'Critical' && x.name != 'Soft')
            .orderByDescending(x => x.countHighFail).thenByDescending(x => x.countMediumFail).thenByDescending(x => x.countLowFail)
            .thenByDescending(x => x.countLowPass + x.countMediumPass + x.countHighPass).toArray();

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.RuleActions;
        const ruleActionsResponse = await this.lookupService.getLookup(request);
        const ruleActions = ruleActionsResponse.activeValues.map(x => {
            return <RuleActionModel>{
                description: x.additionalData.description,
                info: x.additionalData.info,
                link: x.additionalData.link,
                linkTitle: x.additionalData.linkTitle,
                ruleActionId: x.id,
                title: x.additionalData.title
            };
        });
        const failedVetTasks = Enumerable.from(this.item.vetTasks).where(x => x.vetTaskOutcomeId == VetRuleOutcomeStepEnum.Fail);
        this.suggestedActions = failedVetTasks.where(x => !x.isCritical)
            .where(x => x.ruleActionId != null && ruleActions.filter(y => y.ruleActionId == x.ruleActionId && y.title).length > 0)
            .groupBy(x => x.ruleActionId)
            .select(x => {
                const ruleActionId = x.key();
                return ruleActions.filter(x => x.ruleActionId == ruleActionId)[0];
            }).toArray();

        this.requiredActions = failedVetTasks.where(x => x.isCritical && x.ruleActionId != VettingActionStepEnum.VesselHealthCheckCritical)
            .where(x => x.ruleActionId != null && ruleActions.filter(y => y.ruleActionId == x.ruleActionId && y.title).length > 0)
            .groupBy(x => x.ruleActionId)
            .select(x => {
                const ruleActionId = x.key();
                return ruleActions.filter(x => x.ruleActionId == ruleActionId)[0];
            }).toArray();

        this.criticalActions = failedVetTasks.where(x => x.isCritical && x.ruleActionId == VettingActionStepEnum.VesselHealthCheckCritical)
            .groupBy(x => x.title)
            .select(x => {
                const title = x.key();
                const first = x.first();
                return {
                    ruleActionId: -1,
                    title: title,
                    description: first.description,
                    link: null,
                    linkTitle: null,
                    info: null
                };
            }).toArray();

        this.outcome = this.getOutcome();
        this.isLoading = false;
    }


    getOutcome(): OutcomeModel {
        if (this.item.vetStatusId == VetStatusEnum.Processing) {
            return {
                class: "processing",
                title: "Processing",
                description: "Please wait patiently while we process your health check",
                noActionsRequired: false
            };
        }
        if (this.item.vetStatusId == VetStatusEnum.ProcessingOutcome) {
            return {
                class: "processing-outcome",
                title: "Processing Outcome",
                description: "Please wait patiently while we process your health check",
                noActionsRequired: false
            };
        }

        if (this.requiredActions.length > 0)
            return {
                class: "required-actions",
                title: "Actions Required",
                description: "Please review required and/or suggested actions",
                noActionsRequired: false
            };

        if (this.suggestedActions.length > 0)
            return {
                class: "suggested-actions",
                title: "Actions Suggested",
                description: "Please review suggested actions",
                noActionsRequired: false
            };

        if (this.criticalActions.length > 0)
            return {
                class: "failed-critical",
                title: "Health Check Completed",
                description: "Failed critical RS baseline",
                noActionsRequired: false
            };

        if (this.suggestedActions.length == 0 && this.requiredActions.length == 0 && this.criticalActions.length == 0)
            return {
                class: "no-actions-required",
                title: "Health Check Completed",
                description: "No actions required",
                noActionsRequired: true
            };
    }
}
